import { useMemo } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Card } from 'ui-components/components/card';
import { CompletedStepCheckmark } from 'ui-components/components/completed-step-checkmark/completed-step-checkmark';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { DataTable } from '../../../../components/modals/label-batch-status/error-warn/data-table';
import { Steps } from '../context/constants';
import { useReadyToPickContext, useStepInfo } from '../context/ready-to-pick-context';
import { messages } from '../messages';
import style from '../ready-to-pick.module.less';

/**
 * @param {object} props
 * @param {object} props.messages
 * @returns {JSX.Element}
 */
export const VerifyOrdersCard = ({ messages: messagesProp }) => {
  const {
    areAllProblemOrdersSelected,
    areAnyProblemOrdersSelected,
    canVerifyOrdersContinue,
    onVerifyOrdersContinue,
    problemOrders,
    selectedProblemOrderIds,
    removeProblemOrder,
    removeSelectedProblemOrder,
    toggleAllOrdersSelections,
    toggleOrderSelection,
  } = useReadyToPickContext();
  const { isActiveStep, isLoading, isStepComplete } = useStepInfo(Steps.checkForOrderErrors);

  const problemOrdersWithSelected = useMemo(() => {
    return problemOrders.map((order) => ({
      ...order,
      isSelected: selectedProblemOrderIds.includes(order.fulfillmentPlanId),
    }));
  }, [problemOrders, selectedProblemOrderIds]);

  return (
    <Card
      className={classnames(style['step-card'], {
        [style.inactive]: !isActiveStep,
      })}
    >
      <FlexRow className={style['cell-row']} gap={Gap.Space16}>
        <FlexColumn className={style['card-icon']}>
          <CompletedStepCheckmark isComplete={isStepComplete} />
        </FlexColumn>
        <FlexColumn className={style['card-body']} gap={Gap.Space16}>
          <Text textType={TextType.H3} value={messages.checkForOrderErrors} />
          {isActiveStep && !isLoading ? (
            <>
              <Text textType={TextType.Description} value={messagesProp.step2Body} />
              <span className={style['order-errors']}>
                <Button disabled={!areAnyProblemOrdersSelected} onClick={removeSelectedProblemOrder} theme={THEME_CONSTANTS.primary}>
                  <FormattedMessage {...messages.orderErrorsRemoveButton} />
                </Button>
              </span>
              <DataTable
                allRemoved={false}
                className={style['data-table']}
                errorOrWarnCollection={problemOrdersWithSelected}
                hasToggleAllChecked={areAllProblemOrdersSelected}
                onRemoveRow={removeProblemOrder}
                onRowSelected={toggleOrderSelection}
                onToggleAllChecked={toggleAllOrdersSelections}
                showReadyToPickTableLayout
              />
              <span>
                <Button disabled={!canVerifyOrdersContinue} onClick={onVerifyOrdersContinue} theme={THEME_CONSTANTS.primary}>
                  <FormattedMessage {...messages.continue} />
                </Button>
              </span>
            </>
          ) : null}
          {isActiveStep && isLoading ? <LoadingIndicator className={style.loading} /> : null}
        </FlexColumn>
      </FlexRow>
    </Card>
  );
};
